/**
 * The app's single API entry point.
 *
 * Same-origin in production (`/api/*` is routed to the API Worker on the same
 * hostname) and proxied to :8789 in dev, so no base URL is ever configured. Auth is a
 * bearer token from Kinde, attached per request — see lib/auth.tsx.
 *
 * Each fetcher names its 200 response with `InferResponseType<…, 200>`. That is
 * what keeps the types honest: they are derived from the Hono app, so a changed
 * handler breaks this file rather than silently shipping a wrong shape — while
 * excluding the validation-error branch that a bare response type would include.
 */

import { createClient, type InferResponseType, unwrap } from '@ynab/api-client'
import type { Milliunits } from '@ynab/core'
import { getToken } from './lib/auth.tsx'

/*
 * `getToken` rather than a token: it is read on every request, so a rotation the SDK
 * performs underneath is picked up without rebuilding the client. See lib/auth.tsx.
 */
export const api = createClient({ getToken })

/**
 * Re-tags a number from the API as `Milliunits`.
 *
 * JSON has no branded types, so amounts arrive as plain numbers and the brand
 * has to be reapplied at this boundary. Confined to one named helper on purpose:
 * the cast is safe only because the API guarantees integer milliunits, and that
 * assumption should be visible rather than sprinkled through components.
 */
export const money = (amount: number): Milliunits => amount as Milliunits

export const queryKeys = {
  me: ['me'] as const,
  accounts: ['accounts'] as const,
  forecast: (days: number) => ['forecast', days] as const,
  loans: ['loans'] as const,
  cushion: ['cushion'] as const,
  reports: (from: string, to: string) => ['reports', from, to] as const,
  looseEnds: ['loose-ends'] as const,
  fill: ['fill'] as const,
}

export type MeResponse = InferResponseType<typeof api.api.me.$get, 200>
export type AccountsResponse = InferResponseType<typeof api.api.accounts.$get, 200>
export type AccountView = AccountsResponse[number]
export type Member = MeResponse['members'][number]
export type SyncResponse = InferResponseType<typeof api.api.sync.$post, 200>

export async function fetchMe(): Promise<MeResponse> {
  return unwrap(await api.api.me.$get())
}

export async function fetchAccounts(): Promise<AccountsResponse> {
  return unwrap(await api.api.accounts.$get())
}

export async function triggerSync(full = false): Promise<SyncResponse> {
  return unwrap(await api.api.sync.$post({ query: full ? { full: '1' } : {} }))
}

// --- Ahead ---

export type ForecastResponse = InferResponseType<typeof api.api.forecast.$get, 200>
export type AccountForecastView = ForecastResponse['accounts'][number]
export type UpcomingRow = ForecastResponse['upcoming'][number]
export type ForecastPoint = AccountForecastView['points'][number]

export async function fetchForecast(days: number): Promise<ForecastResponse> {
  return unwrap(await api.api.forecast.$get({ query: { days: String(days) } }))
}

// --- Loans ---

export type LoansResponse = InferResponseType<typeof api.api.loans.$get, 200>
export type LoanResponse = LoansResponse[number]
export type LoanProjection = LoanResponse['projection']

export async function fetchLoans(): Promise<LoansResponse> {
  return unwrap(await api.api.loans.$get())
}

export type LoanTermsBody = Parameters<(typeof api.api.loans)[':accountId']['$put']>[0]['json']

export async function setLoanTerms(accountId: string, json: LoanTermsBody): Promise<LoansResponse> {
  return unwrap(await api.api.loans[':accountId'].$put({ param: { accountId }, json }))
}

export async function setLoanCharge(
  transactionId: string,
  kind: 'interest' | 'drawdown' | 'fee' | null,
): Promise<LoansResponse> {
  return unwrap(
    await api.api['loan-charges'][':transactionId'].$put({
      param: { transactionId },
      json: { kind },
    }),
  )
}

// --- Cushion ---

export type CushionResponse = InferResponseType<typeof api.api.cushion.$get, 200>
export type CushionGroup = CushionResponse['groups'][number]
export type CushionCategory = CushionGroup['categories'][number]

export async function fetchCushion(): Promise<CushionResponse> {
  return unwrap(await api.api.cushion.$get())
}

export async function setCategoryFlags(
  categoryId: string,
  json: { livingCost?: boolean; ringFenced?: boolean },
): Promise<CushionResponse> {
  return unwrap(await api.api.categories[':categoryId'].flags.$put({ param: { categoryId }, json }))
}

export async function setSafetyNet(json: {
  categoryId: string | null
  months: number
}): Promise<CushionResponse> {
  return unwrap(await api.api.settings['safety-net'].$put({ json }))
}

// --- Looking back ---

export type ReportsResponse = InferResponseType<typeof api.api.reports.$get, 200>
export type MonthlyFlow = ReportsResponse['months'][number]
export type CategorySpend = ReportsResponse['byCategory'][number]
export type PayeeSpend = ReportsResponse['byPayee'][number]
export type NetWorthPoint = ReportsResponse['netWorth'][number]

export async function fetchReports(from: string, to: string): Promise<ReportsResponse> {
  return unwrap(await api.api.reports.$get({ query: { from, to } }))
}

// --- Loose ends ---

export type LooseEndsResponse = InferResponseType<(typeof api.api)['loose-ends']['$get'], 200>

export async function fetchLooseEnds(): Promise<LooseEndsResponse> {
  return unwrap(await api.api['loose-ends'].$get())
}

// --- Fill ---

export type FillResponse = InferResponseType<typeof api.api.fill.$get, 200>
export type FillAllocation = FillResponse['allocations'][number]
export type FillBody = Parameters<typeof api.api.fill.$post>[0]['json']
export type ApplyFillResponse = InferResponseType<typeof api.api.fill.$post, 200>

export async function fetchFill(): Promise<FillResponse> {
  return unwrap(await api.api.fill.$get())
}

/** Assigns in YNAB. Answers 409 when YNAB has moved since the plan was made. */
export async function applyFill(json: FillBody): Promise<ApplyFillResponse> {
  return unwrap(await api.api.fill.$post({ json }))
}

// --- Ask ---

export type AskBody = Parameters<typeof api.api.ask.$post>[0]['json']
export type AskResponse = InferResponseType<typeof api.api.ask.$post, 200>

export async function askQuestion(json: AskBody): Promise<AskResponse> {
  return unwrap(await api.api.ask.$post({ json }))
}
